import { useState, useRef, useEffect } from 'react';
import { X } from 'lucide-react';

export function OTPModal({ 
    isOpen, 
    onClose, 
    onSubmit, 
    shopName, 
    onResend, 
    otpTimer: externalTimer, 
    errorMessage, 
    isLoading = false 
}) {
    const [otp, setOtp] = useState(['', '', '', '', '', '']);
    const [trustDevice, setTrustDevice] = useState(false);
    const inputRefs = useRef([]);

    // Reset OTP when modal opens
    useEffect(() => {
        if (isOpen) {
            setOtp(['', '', '', '', '', '']);
            setTrustDevice(false);
            if (inputRefs.current[0]) {
                setTimeout(() => {
                    inputRefs.current[0]?.focus();
                }, 100);
            }
        }
    }, [isOpen]);

    // Clear error when OTP changes
    useEffect(() => {
        if (otp.join('').length > 0 && errorMessage) {
            // Error will be cleared by parent component
        }
    }, [otp, errorMessage]);

    // Focus first input on mount
    useEffect(() => {
        if (isOpen && inputRefs.current[0]) {
            inputRefs.current[0].focus();
        }
    }, [isOpen]);

    const handleChange = (index, value) => {
        // Only allow numbers
        if (value && !/^\d$/.test(value)) return;

        const newOtp = [...otp];
        newOtp[index] = value;
        setOtp(newOtp);

        // Auto focus next input
        if (value && index < 5) {
            inputRefs.current[index + 1]?.focus();
        }
    };

    const handleKeyDown = (index, e) => {
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text').slice(0, 6);
        const digits = pastedData.match(/\d/g);

        if (digits) {
            const newOtp = [...otp];
            digits.forEach((digit, i) => {
                if (i < 6) newOtp[i] = digit;
            });
            setOtp(newOtp);

            // Focus last filled input or last input
            const lastIndex = Math.min(digits.length, 5);
            inputRefs.current[lastIndex]?.focus();
        }
    };

    const handleSubmit = () => {
        const otpString = otp.join('');
        if (otpString.length === 6 && !isLoading) {
            onSubmit(otpString, trustDevice);
        }
    };

    const handleResend = () => {
        if (externalTimer <= 0 && onResend) {
            setOtp(['', '', '', '', '', '']);
            setTrustDevice(false);
            inputRefs.current[0]?.focus();
            onResend();
        }
    };

    const handleCancel = () => {
        if (!isLoading) {
            setOtp(['', '', '', '', '', '']);
            setTrustDevice(false);
            onClose();
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[200] p-4">
            <div
                className="bg-white rounded-2xl shadow-2xl w-full max-w-[440px] p-8 relative animate-in fade-in zoom-in duration-200"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Close Button */}
                <button
                    onClick={handleCancel}
                    className="absolute top-4 right-4 p-1 hover:bg-gray-100 rounded-lg transition-colors"
                    aria-label="Close"
                >
                    <X className="size-5 text-gray-500" />
                </button>

                <div className="space-y-5">
                    {/* Heading */}
                    <div className="text-center">
                        <h2 className="text-[22px] font-bold text-gray-900">
                            6 Digit OTP
                        </h2>
                        <p className="text-sm text-gray-500 mt-2">
                            Enter the 6-digit OTP sent to your registered email.
                        </p>
                        <p className="text-xs text-blue-600 mt-1 font-medium">
                            Switching to: {shopName}
                        </p>
                    </div>

                    {/* OTP Input Fields */}
                    <div className="flex justify-center gap-3">
                        {otp.map((digit, index) => (
                            <input
                                key={index}
                                ref={(el) => (inputRefs.current[index] = el)}
                                type="text"
                                maxLength={1}
                                value={digit}
                                onChange={(e) => handleChange(index, e.target.value)}
                                onKeyDown={(e) => handleKeyDown(index, e)}
                                onPaste={handlePaste}
                                className="w-12 h-12 text-center text-xl font-semibold border border-gray-300 rounded-lg focus:border-blue-600 focus:ring-2 focus:ring-blue-100 outline-none transition-all"
                            />
                        ))}
                    </div>

                    {/* Error Message */}
                    {errorMessage && (
                        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-lg text-sm">
                            {errorMessage}
                        </div>
                    )}

                    {/* Resend Section */}
                    <div className="text-center">
                        {externalTimer <= 0 ? (
                            <button
                                onClick={handleResend}
                                disabled={isLoading}
                                className="text-sm text-blue-600 font-medium hover:text-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Resend OTP
                            </button>
                        ) : (
                            <p className="text-sm text-gray-500">
                                Resend OTP in <span className="font-medium">{externalTimer}s</span>
                            </p>
                        )}
                    </div>

                    {/* Checkbox */}
                    <div className="flex items-center gap-2">
                        <input
                            type="checkbox"
                            id="trust-device"
                            checked={trustDevice}
                            onChange={(e) => setTrustDevice(e.target.checked)}
                            className="w-[18px] h-[18px] text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-100 cursor-pointer"
                        />
                        <label
                            htmlFor="trust-device"
                            className="text-sm text-gray-700 cursor-pointer select-none"
                        >
                            Trust this device
                        </label>
                    </div>

                    {/* Buttons */}
                    <div className="flex gap-3 pt-2">
                        <button
                            onClick={handleCancel}
                            disabled={isLoading}
                            className="flex-1 h-11 px-4 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSubmit}
                            disabled={otp.join('').length !== 6 || isLoading}
                            className="flex-1 h-11 px-4 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                        >
                            {isLoading ? (
                                <span className="flex items-center gap-2">
                                    <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Verifying...
                                </span>
                            ) : (
                                'Submit'
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
